import { useState } from 'react';

import { CloseIcon } from '@braskai/icons';
import { Button, Modal, Typography } from '@braskai/ui-kit';

import { useAfterPurchaseModalStore } from '@/entities/afterPurchase';
import { useCurrentUser } from '@/entities/user/currentUser';
import { useReliableUserRefresh } from '@/entities/user/currentUser/model/useReliableUserRefresh';

import { API } from '@/shared/api';
import { FeaturedResumeIcon } from '@/shared/assets/icons/FeaturedResumeIcon';
import { useInterfaceLanguage } from '@/shared/i18n';
import { formatInterfaceCurrency, formatInterfaceDate } from '@/shared/i18n/formatLocale';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { useCancelPauseModalStore } from '../model/useCancelPauseModalStore';
import { SActions, SCloseButton, SContent, SHeader, SHeaderTop, SModalContainer, SPlanSection } from './styled';

export const CancelPauseModal = () => {
  const { locale, t } = useInterfaceLanguage();
  const isOpen = useCancelPauseModalStore((state) => state.isOpen);
  const close = useCancelPauseModalStore((state) => state.close);
  const onSuccess = useCancelPauseModalStore((state) => state.onSuccess);

  const { data: user } = useCurrentUser();
  const { reliableRefresh } = useReliableUserRefresh();
  const { openSuccess } = useAfterPurchaseModalStore();

  const [isLoading, setIsLoading] = useState(false);

  const userPriceData = user?.subscription?.plan;
  const pausedUntil = user?.subscription?.payments_collection_paused_until;
  const currentPeriodEnd = user?.subscription?.current_period_end;
  const formattedCurrentPlanPrice = formatInterfaceCurrency(
    locale,
    userPriceData?.amount ?? 0,
    userPriceData?.currency ?? 'usd',
  );
  const intervalLabel = t(userPriceData?.interval === 'year' ? 'pricing.change.yearly' : 'pricing.change.monthly');
  const nextBillingDate = t('pricing.pause.nextBillingDate');

  const handleResume = async () => {
    MixpanelEvents.renewSubscriptionPauseClick();

    try {
      setIsLoading(true);

      const response = await API.resumeSubscription();

      if (response.ok) {
        close();
        void reliableRefresh();
        onSuccess?.();
        openSuccess(t('pricing.pause.success'));
        MixpanelEvents.renewSubscriptionPauseDone();
      }
    } catch (e) {
      showErrorAlert(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    close();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <SModalContainer>
        <SHeader>
          <SHeaderTop>
            <FeaturedResumeIcon height={56} width={56} />
            <SCloseButton
              aria-label={t('pricing.modal.closeAria')}
              data-testid="cancel-pause-modal-close"
              onClick={handleClose}
            >
              <CloseIcon height={24} width={24} />
            </SCloseButton>
          </SHeaderTop>
        </SHeader>

        <SContent>
          <SPlanSection>
            <Typography color="grey.900" variant="body-1-md">
              {t('pricing.pause.title')}
            </Typography>
            <Typography color="grey.700" variant="body-2">
              {t('pricing.pause.description', {
                date: pausedUntil ? formatInterfaceDate(locale, pausedUntil) : nextBillingDate,
              })}
            </Typography>
          </SPlanSection>

          <SPlanSection>
            <Typography color="primary.600" variant="body-1-md">
              {t('pricing.pause.resumeTitle')}
            </Typography>
            <Typography color="grey.700" variant="body-2">
              {t('pricing.pause.chargeDescription', {
                date: currentPeriodEnd ? formatInterfaceDate(locale, currentPeriodEnd) : nextBillingDate,
                interval: intervalLabel,
                price: formattedCurrentPlanPrice,
              })}
            </Typography>
          </SPlanSection>
        </SContent>

        <SActions>
          <Button
            data-testid="cancel-pause-modal-resume-subscription-now"
            disabled={isLoading}
            isLoading={isLoading}
            size="lg"
            fullWidth
            onClick={handleResume}
          >
            {t('pricing.pause.resumeNow')}
          </Button>
        </SActions>
      </SModalContainer>
    </Modal>
  );
};
